import React, { createContext, useContext, useState, ReactNode } from "react";
import CardDialog from "../dialogs/Card";
import { Card } from "../interfaces/Card";
import { Flow } from "../interfaces/Flow";
import { useAuth } from "./auth";
import { FormAnswer } from "../interfaces/FormAnswer";
import { Register } from "../interfaces/Register";
import FormAnswerView from "../dialogs/FormAnswerView";

interface CardDialogItem {
    card: any;
    flow: any;
    options?: {
        showFlowDetail?: boolean;
        isMobile?: boolean;
        onClose?: () => void;
    };
}

interface RegisterDialogItem {
    formAnswer: FormAnswer;
    register: Register;
    options?: {
        onClose?: () => void;
    };
}

interface CardDialogContextData {
    openCard: (card: Card, flow: Flow, options?: CardDialogItem["options"]) => void;
    closeTopCard: () => void;
    closeAllCards: () => void;

    openRegister: (formAnswer: FormAnswer, register: Register, options?: RegisterDialogItem["options"]) => void;
    closeTopRegister: () => void;
    closeAllRegisters: () => void;
}

const CardDialogContext = createContext<CardDialogContextData | undefined>(undefined);

export const CardDialogProvider = ({ children }: { children: ReactNode }) => {

    const [stackCard, setStackCard] = useState<CardDialogItem[]>([]);
    const [stackRegister, setStackRegister] = useState<RegisterDialogItem[]>([]);
    const { user } = useAuth();

    const openCard = (card: Card, flow: Flow, options?: CardDialogItem["options"]) => {

        let canAcess = true;

        //Valid the typeUser 
        if (flow.typeUserAccess !== undefined) {
            // When is Visão Restrita and isn't the responsible of the card
            if (flow.typeUserAccess === "R" && card.user_id !== user.id_user) {
                canAcess = false;
            }

            // When is Solicitante and isn't the creator of the card
            if (flow.typeUserAccess === "S" && card.user_id_creator !== user.id_user && card.user_id !== user.id_user) {
                canAcess = false;
            }
        }

        if (canAcess) {
            setStackCard(prev => [...prev, { card, flow, options }]);
        }
    };

    const closeTopCard = () => {
        setStackCard(prev => {
            const updated = [...prev];
            const top = updated.pop();

            if (top?.options?.onClose) {
                top.options.onClose();
            }

            return updated;
        });
    };

    const closeAllCards = () => {
        setStackCard([]);
    };

    const openRegister = (formAnswer: FormAnswer, register: Register, options?: RegisterDialogItem["options"]) => {

        let canAcess = true;

        //Valid the typeUser 
        //Rule to acess TODO  

        if (canAcess) {
            setStackRegister(prev => [...prev, { formAnswer, register, options }]);
        }
    };

    const closeTopRegister = () => {
        setStackRegister(prev => {
            const updated = [...prev];
            const top = updated.pop();

            if (top?.options?.onClose) {
                top.options.onClose();
            }

            return updated;
        });
    };

    const closeAllRegisters = () => {
        setStackRegister([]);
    };


    return (
        <CardDialogContext.Provider value={{ openCard, closeTopCard, closeAllCards, openRegister, closeTopRegister, closeAllRegisters }}>
            {children}
            {stackCard.map((item, index) => (
                <CardDialog
                    key={index}
                    open={true}
                    flow={item.flow}
                    isMobile={item.options?.isMobile ?? false}
                    selectedValue={item.card}
                    onClose={closeTopCard}
                    typeUser={item.flow?.typeUserAccess ?? "V"}
                    showFlowDetail={item.options?.showFlowDetail ?? true}
                />
            ))}
            {stackRegister.map((item, index) => (
                <FormAnswerView
                    key={index}
                    open={true}
                    register={item.register}
                    formAnswer={item.formAnswer}
                    onClose={closeTopRegister}
                />
            ))}
        </CardDialogContext.Provider>
    );
};

export const useCardDialog = () => {
    const context = useContext(CardDialogContext);
    if (!context) {
        throw new Error("useCardDialog must be used within a CardDialogProvider");
    }
    return context;
};